/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { addClass, hasClass } from '@grapecity/wijmo';
import { DataMap, CellType, CellRangeEventArgs, HeadersVisibility } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { SetupStep } from 'models/setup/SetupStep';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import { CrudCode } from 'models/common/Edit';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { findProjectCommonStep, findSetupStep, saveProjectCommonStep } from 'apis/setup/SetupStep';
import { SuccessOrNot } from 'models/common/RestApi';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회 > 프로젝트 셋업단계 설정 > [단계 유형/공통단계] 탭 > 공통단계 그리드
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 * 2026.08.25   jshwang12       단계 유형/공통단계 저장 수정 (개별 저장 -> 통합 저장)
 */

interface Props {
  isReadOnly?: boolean;
}

const CommonStepGrid = ({ isReadOnly = true, ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [elmPrjId, setElmPrjId] = useState<string>();
  const [rowData, setRowData] = useState<SetupStep[]>([]);
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const dragIndex = useRef<any>();

  useImperativeHandle(ref, () => ({
    search: (params) => {
      setElmPrjId(params?.elmPrjId);
      handleSearch(params?.elmPrjId);
    },
    hasChanges: () => (rowData || []).findIndex((o) => !!o.crudKey) > -1,
    validate: () => {
      return validate();
    },
    getConfirmMessages: () => getConfirmMessages(),
    save: () => {
      return new Promise((resolve) => {
        handleSave().then((res) => resolve(res));
      });
    },
    refresh: () => handleSearch(),
  }));

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = isReadOnly
        ? HeadersVisibility.Column
        : HeadersVisibility.All;
    }
  }, [isReadOnly]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 셋업구분
    ]).then(([prjSpDivsCd]) => {
      setCode({
        prjSpDivsCd: prjSpDivsCd,
      });
    });
  };

  const handleAddRow = () => {
    const newData = {
      crudKey: CrudCode.CREATE,
      istlDivsCd: '',
      prjSpCd: '',
    };
    setRowData((prev) => [newData, ...prev]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleSearch = (params = '') => {
    const id = params || elmPrjId;
    if (id) {
      Promise.all([
        findSetupStep({ useYn: 'Y' }), // 전체 단계목록
        findProjectCommonStep({ elmPrjId: id }),
      ]).then(([prjSpCd, steps]) => {
        setPrjSpCds(prjSpCd);
        setRowData(steps);
      });
    } else {
      setRowData([]);
    }
  };

  const validate = () => {
    if ((rowData || []).length < 1) {
      return true;
    }
    const seen = new Map<string, number>();
    const dupKeys = [] as string[];
    (rowData || []).forEach((item) => {
      if (item.prjSpCd) {
        const count = seen.get(item.prjSpCd) || 0;
        seen.set(item.prjSpCd, count + 1);
        if (count === 1) {
          dupKeys.push(item.prjSpCd);
        }
      }
    });

    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        if (_.isEmpty(o.istlDivsCd)) {
          return `${index + 1} : ${t(
            'setup.label.셋업 구분을 선택해 주세요.',
            '셋업 구분을 선택해 주세요.'
          )}`;
        }
        if (_.isEmpty(o.prjSpCd)) {
          return `${index + 1} : ${t(
            'setup.label.셋업 단계명을 선택해 주세요.',
            '셋업 단계명을 선택해 주세요.'
          )}`;
        }
        if (dupKeys.includes(o.prjSpCd || '')) {
          const cd = (prjSpCds || []).find((c) => c.prjSpCd === o.prjSpCd);
          return `${index + 1} : ${t(
            'setup.label.중복된 셋업 단계 코드가 있습니다.',
            '중복된 셋업 단계 코드가 있습니다.'
          )} (${cd?.prjSpNm || o.prjSpCd})`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }
    return true;
  };

  /**
   * 저장 확인 모달에 추가로 노출할 안내 메시지
   */
  const getConfirmMessages = () => {
    const msg = [] as string[];
    // 추가한 항목이 있는 경우
    if ((rowData || []).findIndex((o) => CrudCode.CREATE === o.crudKey) > -1) {
      msg.push(
        t(
          'setup.label.공통단계 추가 시 일괄적용되며 저장 후 셋업단계에서 개별 공통단계 순서를 조정해주세요.',
          '공통단계 추가 시 일괄적용되며 저장 후 셋업단계에서 개별 공통단계 순서를 조정해주세요.'
        )
      );
    }
    // 삭제한 항목이 있는 경우
    if ((rowData || []).findIndex((o) => CrudCode.DELETE === o.crudKey) > -1) {
      msg.push(
        t(
          'setup.label.공통단계 삭제 시 셋업단계에서는 일반단계로 남아있습니다.',
          '공통단계 삭제 시 셋업단계에서는 일반단계로 남아있습니다.'
        )
      );
    }
    return msg;
  };

  const handleSave = () => {
    return new Promise((resolve) => {
      if ((rowData || []).findIndex((o) => !!o.crudKey) < 0) {
        resolve({ successOrNot: SuccessOrNot.Y });
        return;
      }
      const rows = (rowData || []).reduce(
        (acc, cur) => [...acc, { ...cur, elmPrjId: elmPrjId }],
        [] as SetupStep[]
      );
      saveProjectCommonStep(rows)
        .then((res) => {
          resolve(res);
        })
        .catch(() => resolve({ successOrNot: SuccessOrNot.N }));
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.공통 단계 목록', '공통 단계 목록'),
      t('setup.label.공통 단계 목록', '공통 단계 목록')
    );
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (['prjSpCd', 'istlDivsCd'].includes(binding)) {
        // 수정 시 키값 수정불가
        const readOnly = CrudCode.CREATE !== item.crudKey;
        cell.ariaReadOnly = readOnly;
        if (readOnly) {
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    if (isReadOnly) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;
      // 수정 시 키값 수정불가
      if (['prjSpCd', 'istlDivsCd'].includes(binding)) {
        if (CrudCode.CREATE !== item.crudKey) {
          e.cancel = true;
        }
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    // 셋업 구분
    if ('istlDivsCd' === binding) {
      const newIstlDivsCd =
        (code?.prjSpDivsCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      // 값이 변한 경우 초기화
      if (oldVal !== newIstlDivsCd) {
        item.prjSpCd = '';
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.headersVisibility = isReadOnly ? HeadersVisibility.Column : HeadersVisibility.All;

    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (item?.usedYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
  });

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapPrjSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링
      return (prjSpCds || [])
        .filter((o) => o.istlDivsCd === item.istlDivsCd)
        .map((o) => o.prjSpNm || '');
    };
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: isReadOnly,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: !isReadOnly,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서', '순서')),
        minWidth: 43,
        width: 43,
        visible: !isReadOnly,
        isReadOnly: true,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
      },
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.셋업 구분', '셋업 구분')),
        align: 'center',
        minWidth: 80,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계 명', '셋업 단계 명')),
        align: 'center',
        minWidth: 40,
        width: '*',
        cssClass: 'WijmoSelect',
        dataMap: mapPrjSpCd,
      },
    ];
  }, [code, prjSpCds, isReadOnly]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.공통 단계', '공통 단계')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {!isReadOnly && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={() => handleSearch()}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={518}
        allowPinning={false}
        allowSorting={false}
        excludeFilter={['sortable']}
        isReadOnly={isReadOnly}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
        initialized={onInitialized}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            if (item.crudKey !== CrudCode.CREATE) {
              item.crudKey = CrudCode.UPDATE;
            }
          });
        }}
      />
    </>
  );
};

export default React.forwardRef(CommonStepGrid);
